import { Box, Cyl, Slab } from '../../render/parts'
import { Whiteboard } from '../parts/props'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * WALL ITEM — origin on the wall plane, back face at z = 0, growing into +Z.
 *
 * `Whiteboard` is centred on z = 0 (its 30 mm frame spans z ∈ [-0.015, 0.015],
 * with the writing face and the accent note layered in front), so the board is
 * pushed forward by BOARD_HALF to land its back on the wall, and the pen tray
 * hangs off the bottom edge from there.
 */
const BOARD_HALF = 0.015
/** Front face of the board once it sits on the wall plane. */
const FACE_Z = 0.04

/** Board centre offset, so board + tray stay inside the declared 1.1 m height. */
const BOARD_Y = 0.02

const WIDE = { width: 1.6, height: 1.05 }
const NARROW = { width: 1.2, height: 0.9 }

/** Aluminium marker tray with a front lip, two brackets and the usual clutter. */
function PenTray({ width, top, accent }: { width: number; top: number; accent?: string }) {
  const w = Math.min(width * 0.42, 0.62)
  return (
    <group position={[0, top, 0]}>
      {/* Shelf: [x, depth into the room, thickness]. Its back edge stops 1 mm
          short of the wall so it never shares a plane with it. */}
      <Slab tone="metal" size={[w, 0.062, 0.02]} radius={0.005} position={[0, -0.01, FACE_Z - 0.008]} />
      {/* Front lip, embedded 6 mm into the shelf and set back from its front edge. */}
      <Box tone="metal" size={[w, 0.028, 0.008]} position={[0, 0.008, FACE_Z + 0.018]} />
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="metalDark"
          size={[0.026, 0.03, 0.03]}
          position={[side * (w / 2 - 0.05), -0.021, FACE_Z - 0.004]}
        />
      ))}
      {/* Markers and eraser lying in the tray. */}
      <Cyl tone="accent" color={accent} axis="x" r={0.008} h={0.13} segments={10} position={[-w * 0.2, 0.006, FACE_Z + 0.004]} />
      <Cyl tone="darkSoft" axis="x" r={0.008} h={0.13} segments={10} position={[-w * 0.02, 0.006, FACE_Z + 0.004]} />
      <Box tone="darkSoft" size={[0.09, 0.024, 0.04]} position={[w * 0.26, 0.008, FACE_Z - 0.008]} />
    </group>
  )
}

function WhiteboardWall({ accent, variant = 'wide' }: AssetContext) {
  const { width, height } = variant === 'narrow' ? NARROW : WIDE

  return (
    <group>
      <group position={[0, BOARD_Y, BOARD_HALF]}>
        <Whiteboard width={width} height={height} accent={accent} />
      </group>
      <PenTray width={width} top={BOARD_Y - height / 2 - 0.006} accent={accent} />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'whiteboard-wall',
  name: 'Pizarra',
  category: 'decor',
  footprint: [1.65, 0.07],
  height: 1.1,
  anchor: 'wall',
  mountHeight: 1.5,
  accentable: true,
  variants: [
    { id: 'wide', name: '1,6 m' },
    { id: 'narrow', name: '1,2 m' },
  ],
  keywords: ['pizarra', 'tablero', 'whiteboard', 'board', 'marcador', 'sala de reuniones'],
  Component: WhiteboardWall,
}
